import React, { useState, useEffect, useMemo } from 'react';
import { createManualOrder, sendCartToCustomer, getSettings, updateOrder, updateProductStock } from '../services/apiServices';
import ProductSelector from './ProductSelector'; // Importa o novo componente
import styles from './RegisterSaleModal.module.css';

const RegisterSaleModal = ({ isOpen, onClose, products = {}, isMobileView = false, mode = 'sale', initialData = null }) => {
  // Estado dos campos do formulário
  const [customerName, setCustomerName] = useState('Balcão');
  const [customerId, setCustomerId] = useState(''); // Telefone
  const [deliveryType, setDeliveryType] = useState('counter');
  const [address, setAddress] = useState({ streetAndNumber: '', neighborhood: '' });
  const [paymentMethod, setPaymentMethod] = useState('Dinheiro');
  const [isRuralDelivery, setIsRuralDelivery] = useState(false); // Novo estado para entrega rural
  const [needsChange, setNeedsChange] = useState(false);
  const [changeFor, setChangeFor] = useState('');
  const [observation, setObservation] = useState(''); // Novo estado para observação
  const [fulfillmentDate, setFulfillmentDate] = useState(''); // NOVO: Estado para data da encomenda
  const [isPreOrderSale, setIsPreOrderSale] = useState(false); // NOVO: Para diferenciar o tipo de venda
  const [paymentConfirmed, setPaymentConfirmed] = useState(true); // Novo estado para status do pagamento
  const [activeCategory, setActiveCategory] = useState('assados'); // Estado para controlar a aba ativa

  // Estado para gerenciamento do carrinho
  const [cart, setCart] = useState([]);
  // Estado de controle do componente
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false); // Novo estado para o popup de sucesso
  const [registeredOrder, setRegisteredOrder] = useState(null); // Novo estado para armazenar o pedido criado/editado para impressão
  const [deliverySettings, setDeliverySettings] = useState(null); // Estado para taxas dinâmicas

  // Carrega as configurações de taxa de entrega ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      getSettings('delivery')
        .then(settings => setDeliverySettings(settings))
        .catch(err => console.error('Erro ao carregar taxas de entrega:', err));
    }
  }, [isOpen]);

  // Limpa o formulário quando o modal é fechado
  useEffect(() => {
    if (!isOpen) {
      // Limpa o formulário quando o modal é fechado
      setCustomerName('Balcão');
      setCustomerId('');
      setDeliveryType('counter');
      setAddress({ streetAndNumber: '', neighborhood: '' });
      setPaymentMethod('Dinheiro');
      setNeedsChange(false);
      setIsRuralDelivery(false);
      setChangeFor('');
      setCart([]);
      setFulfillmentDate(''); // Limpa a data da encomenda
      setIsPreOrderSale(false); // Reseta o tipo de venda
      setObservation('');
      setPaymentConfirmed(true);
      setActiveCategory('assados'); // Reseta a aba para o padrão
      setError(null);
      setRegisteredOrder(null); // Limpa o pedido registrado
    }
  }, [isOpen]);

  // Preenche os dados se houver initialData (usado no modo push_cart)
  useEffect(() => {
    if (isOpen && initialData) {
      if (mode === 'edit') {
        // Modo Edição: Preenche tudo com os dados do pedido existente
        setCustomerName(initialData.customerName || '');
        setCustomerId(initialData.customerId || '');
        setCart(initialData.cart || []);
        setDeliveryType(initialData.deliveryType || 'pickup');
        setAddress(initialData.address || { streetAndNumber: '', neighborhood: '' });
        // Tenta extrair o método de pagamento base (remove "Troco para...")
        const paymentBase = initialData.paymentMethod ? initialData.paymentMethod.split(' (')[0] : 'Dinheiro';
        setPaymentMethod(paymentBase);
        setObservation(initialData.observation || '');
        setFulfillmentDate(initialData.fulfillmentDate || '');
        
        // CORREÇÃO: Verifica se é realmente uma encomenda (flag explícita ou data futura).
        // Pedidos do dia (com data de hoje) devem usar o estoque normal ('stock'), não 'preOrderStock'.
        const today = new Date();
        const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
        const isFuture = initialData.fulfillmentDate && initialData.fulfillmentDate > todayStr;
        setIsPreOrderSale(initialData.isPreOrder || isFuture);
        
        setPaymentConfirmed(initialData.paymentConfirmed || false);
      } else {
        // Modo Push Cart ou Sale (se initialData for cliente ou dados de estoque)
        setCustomerName(initialData.name || 'Balcão');
        setCustomerId(initialData.id || '');
        // Se houver um carrinho pré-montado (vindo do StockStatus), preenchemos aqui
        if (initialData.cart && initialData.cart.length > 0) {
          setCart(initialData.cart);
        }
      }
    }
  }, [isOpen, initialData, mode]);

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    onClose(); // Fecha o modal principal ao clicar em K
  };

  // Organiza os produtos por categoria usando useMemo para otimização
  const categorizedProducts = useMemo(() => {
    const stockType = isPreOrderSale ? 'preOrderStock' : 'stock';
    const categories = { assados: [], acompanhamentos: [], bebidas: [] };
    Object.entries(products).forEach(([name, details]) => {
      // Mapeamento de categorias do banco para as abas do modal
      let targetTab = null;
      if (['bovina', 'suina', 'frango', 'assados'].includes(details.category)) targetTab = 'assados';
      else if (details.category === 'acompanhamentos') targetTab = 'acompanhamentos';
      else if (details.category === 'bebidas') targetTab = 'bebidas';

      if (targetTab) {
        // Se tiver variações, adiciona cada uma como um item selecionável
        if (details.variations && details.variations.length > 0) {
          details.variations.forEach(v => {
            const currentStock = Number(v[stockType]) || 0;
            // Filtra apenas se tiver estoque positivo
            if (currentStock > 0) {
              const fullName = `${name} - ${v.name}`;
              categories[targetTab].push({ 
                name: `${fullName} (R$ ${v.price.toFixed(2)})`,
                cartName: fullName, 
                price: v.price, 
                originalName: name,
                stock: currentStock
              });
            }
          });
        } else {
          // Produto simples
          const currentStock = Number(details[stockType]) || 0;
          // Filtra apenas se tiver estoque positivo
          if (currentStock > 0) {
            categories[targetTab].push({ 
              name: `${name} (R$ ${details.price.toFixed(2)})`,
              cartName: name, 
              ...details,
              stock: currentStock
            });
          }
        }
      }
    });
    // Ordena os produtos dentro de cada categoria
    Object.keys(categories).forEach(cat => {
      categories[cat].sort((a, b) => a.name.localeCompare(b.name));
    });
    return categories;
  }, [products, isPreOrderSale]);

  const handleAddProduct = (productName, quantity) => {
    if (!productName || quantity <= 0) return;
  
    // Tenta encontrar o produto direto (simples) ou na lista processada (variação)
    let price = 0;
    let finalProductName = productName;

    if (products[productName]) {
      price = products[productName].price;
    } else {
      // Procura na lista da categoria ativa (para pegar o preço da variação)
      const found = categorizedProducts[activeCategory]?.find(p => p.name === productName);
      if (found) {
        price = found.price;
        if (found.cartName) finalProductName = found.cartName; // Usa o nome limpo se disponível
      }
    }

    const newItem = {
      product: finalProductName,
      quantity: parseInt(quantity, 10),
      price: price,
    };
    setCart(prevCart => [...prevCart, newItem]);
  };

  const handleRemoveItem = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // Handler para mudança de tipo de entrega
  const handleDeliveryTypeChange = (type) => {
    setDeliveryType(type);
    if (type === 'counter') {
      setCustomerName('Balcão');
      setPaymentConfirmed(true); // Vendas de balcão geralmente são pagas na hora
    } else {
      // Se o nome estava como 'Balcão' e mudou o tipo, limpa o nome para o usuário digitar
      if (customerName === 'Balcão') setCustomerName('');
      setPaymentConfirmed(false); // Desmarca como pago quando for entrega ou retirada
    }
  };

  // --- CÁLCULOS (Movidos para antes do handleSubmit para serem usados nele) ---
  const subtotal = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
  
  const deliveryFee = useMemo(() => {
    if (deliveryType !== 'delivery') return 0;
    
    // 1. Tenta encontrar taxa específica pelo bairro (case-insensitive)
    if (deliverySettings && deliverySettings.fees && address.neighborhood) {
      const normalizedInput = address.neighborhood.trim().toLowerCase();
      const foundFee = deliverySettings.fees.find(f => f.neighborhood.trim().toLowerCase() === normalizedInput);
      if (foundFee) {
        return foundFee.fee;
      }
    }

    // 2. Se não achar bairro, usa a lógica de checkbox rural ou padrão
    return isRuralDelivery ? 15.00 : 6.00;
  }, [deliveryType, isRuralDelivery, address.neighborhood, deliverySettings]);

  const total = subtotal + deliveryFee;
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // --- VALIDAÇÃO DE ESTOQUE CLIENT-SIDE ---
    if (mode === 'sale' || mode === 'edit') {
      const stockType = isPreOrderSale ? 'preOrderStock' : 'stock';
      
      // Agrupa as quantidades do carrinho atual (caso o mesmo item tenha sido adicionado mais de uma vez)
      const aggregatedCart = {};
      cart.forEach(item => {
        aggregatedCart[item.product] = (aggregatedCart[item.product] || 0) + Number(item.quantity);
      });

      for (const [productName, requestedQty] of Object.entries(aggregatedCart)) {
        let currentStock = 0;
        let found = false;

        // Busca o estoque atual do produto simples
        if (products[productName]) {
          currentStock = Number(products[productName][stockType]) || 0;
          found = true;
        } else {
          // Busca o estoque da variação
          for (const [key, details] of Object.entries(products)) {
            if (details.variations) {
              const v = details.variations.find(v => `${key} - ${v.name}` === productName);
              if (v) {
                currentStock = Number(v[stockType]) || 0;
                found = true;
                break;
              }
            }
          }
        }

        // Se for edição, soma o estoque que já estava reservado no pedido original para não dar falso positivo
        let previouslyReserved = 0;
        if (mode === 'edit' && initialData && initialData.cart) {
          const prevItems = initialData.cart.filter(i => i.product === productName);
          previouslyReserved = prevItems.reduce((acc, i) => acc + Number(i.quantity), 0);
        }

        const effectiveStock = found ? (currentStock + previouslyReserved) : 0;

        if (requestedQty > effectiveStock) {
          setError(`Estoque insuficiente para: ${productName}. Disponível: ${effectiveStock}.`);
          setLoading(false);
          return; // Aborta o envio
        }
      }
    }

    // --- MODO PUSH CART (Enviar Carrinho) ---
    if (mode === 'push_cart') {
      try {
        await sendCartToCustomer(customerId, cart);
        setShowSuccess(true);
      } catch (err) {
        setError('Falha ao enviar carrinho para o cliente.');
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    // --- MODO EDIT (Editar Pedido Existente) ---
    if (mode === 'edit') {
      // Prepara os dados atualizados (mesma lógica do create, mas chama update)
      // ... (cálculo de finalPaymentMethod e orderData igual ao create)
    }

    // --- MODO SALE (Venda Manual Completa) ---
    let finalPaymentMethod = paymentMethod;
    if (paymentMethod === 'Dinheiro' && needsChange && changeFor) {
      finalPaymentMethod = `Dinheiro (Troco para: R$ ${changeFor})`;
    }

    // Usa as variáveis calculadas acima (subtotal, deliveryFee, total)
    const orderData = {
      customerName,
      customerId: customerId || null,
      cart,
      deliveryType,
      address: deliveryType === 'delivery' ? address : {},
      deliveryFee, // Adiciona a taxa de entrega
      paymentMethod: finalPaymentMethod,
      observation, // Adiciona observação
      fulfillmentDate: fulfillmentDate || null, // Adiciona a data da encomenda
      isPreOrder: isPreOrderSale, // NOVO: Informa ao backend que é uma encomenda
      paymentConfirmed, // Adiciona status do pagamento
      subtotal,
      total,
    };

    try {
      if (mode === 'edit') {
        // --- LÓGICA DE ATUALIZAÇÃO DE ESTOQUE (COMPARAR ANTES E DEPOIS) ---
        // 1. Calcula a diferença de quantidade para cada produto
        const stockAdjustments = {};

        // Devolve o estoque do carrinho antigo (como se tivesse cancelado)
        if (initialData.cart) {
          initialData.cart.forEach(item => {
            stockAdjustments[item.product] = (stockAdjustments[item.product] || 0) + Number(item.quantity);
          });
        }

        // Deduz o estoque do novo carrinho (como se fosse uma nova venda)
        cart.forEach(item => {
          stockAdjustments[item.product] = (stockAdjustments[item.product] || 0) - Number(item.quantity);
        });

        // 2. Aplica as atualizações apenas onde houve mudança (delta != 0)
        const updatePromises = Object.entries(stockAdjustments).map(async ([productName, delta]) => {
          if (delta === 0) return;

          // Encontra o produto original e variação para saber o estoque atual
          let pName = null;
          let vName = null;
          let currentStock = 0;

          // Tenta achar produto simples
          if (products[productName]) {
            pName = productName;
            currentStock = Number(products[productName].stock) || 0;
          } else {
            // Tenta achar variação (iterando pelos produtos)
            for (const [key, details] of Object.entries(products)) {
              if (details.variations) {
                const v = details.variations.find(v => `${key} - ${v.name}` === productName);
                if (v) {
                  pName = key;
                  vName = v.name;
                  currentStock = Number(v.stock) || 0;
                  break;
                }
              }
            }
          }

          if (pName) {
            const newStock = currentStock + delta;
            // Atualiza o estoque (se for pedido do dia, assume estoque padrão)
            await updateProductStock(pName, newStock, vName);
          }
        });

        await Promise.all(updatePromises);
        // -------------------------------------------------------------------

        // Chama a função de atualização passando o ID do pedido original
        const updated = await updateOrder(initialData.id, orderData);
        setRegisteredOrder(updated || { ...initialData, ...orderData });
        setShowSuccess(true);
      } else {
        const created = await createManualOrder(orderData);
        setRegisteredOrder(created || orderData);
        setShowSuccess(true);
      }
    } catch (err) {
      setError(`Falha ao ${mode === 'edit' ? 'atualizar' : 'registrar'} a venda. Verifique os dados e tente novamente.`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return new Date().toLocaleDateString('pt-BR');
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR');
  };

  const handlePrint = () => {
    const orderToPrint = registeredOrder || {
      orderNumber: initialData?.orderNumber || 'S/N',
      customerName,
      customerId,
      timestamp: new Date().toISOString(),
      cart,
      deliveryType,
      address,
      observation,
      paymentMethod: paymentMethod === 'Dinheiro' && needsChange && changeFor ? `Dinheiro (Troco para: R$ ${changeFor})` : paymentMethod,
      deliveryFee,
      paymentConfirmed,
      total
    };

    const createReceiptHTML = () => {
      let html = '<h2>Casa de Carnes Bom Jesus</h2>';
      if (orderToPrint.orderNumber && orderToPrint.orderNumber !== 'S/N') {
        html += `<div style="font-size: 22px; font-weight: bold; margin-bottom: 5px; text-align: center;">PEDIDO N° ${orderToPrint.orderNumber}</div>`;
      }
      html += `<div style="font-size: 18px; font-weight: bold; margin-bottom: 5px; text-align: center;">${orderToPrint.customerName}</div>`;
      if (orderToPrint.customerId) {
        html += `<div style="font-size: 16px; margin-bottom: 5px; text-align: center;">${orderToPrint.customerId}</div>`;
      }
      html += `Data: ${formatDate(orderToPrint.timestamp)} - ${formatTime(orderToPrint.timestamp)}<br>`;
      html += '<hr>';

      html += '<h3>Itens do Pedido</h3>';
      if (orderToPrint.cart && orderToPrint.cart.length > 0) {
        orderToPrint.cart.forEach(item => {
          const itemTotal = item.quantity * item.price;
          html += `<div style="font-size: 16px; font-weight: bold; margin-bottom: 4px;">${item.quantity}x ${item.product} - R$ ${itemTotal.toFixed(2).replace('.', ',')}</div>`;
        });
      }
      html += '<hr>';

      if (orderToPrint.deliveryType === 'delivery' && orderToPrint.address && orderToPrint.address.streetAndNumber) {
        html += '<h3>Entrega</h3>';
        html += `<div style="font-size: 16px; font-weight: bold;">${orderToPrint.address.streetAndNumber || ''}</div>`;
        if (orderToPrint.address.neighborhood) {
          html += `<div style="font-size: 16px; font-weight: bold;">${orderToPrint.address.neighborhood || ''}</div>`;
        }
        if (orderToPrint.address.reference) {
          html += `Ref: ${orderToPrint.address.reference}<br>`;
        }
        html += '<hr>';
      }

      if (orderToPrint.observation) {
        html += '<h3>Observação</h3>';
        html += `<div style="font-size: 16px; font-weight: bold;">${orderToPrint.observation}</div>`;
        html += '<hr>';
      }

      if (orderToPrint.deliveryTime) {
        html += '<h3>Horário de Entrega</h3>';
        html += `<div style="font-size: 16px; font-weight: bold;">${orderToPrint.deliveryTime}</div>`;
        html += '<hr>';
      }

      if (orderToPrint.pickupTime) {
        html += '<h3>Horário de Retirada</h3>';
        html += `<div style="font-size: 16px; font-weight: bold;">${orderToPrint.pickupTime}</div>`;
        html += '<hr>';
      }

      html += '<h3>Total</h3>';
      html += `Pagamento: ${orderToPrint.paymentMethod}<br>`;
      if (orderToPrint.deliveryType === 'delivery') {
        html += `Taxa de Entrega: R$ ${Number(orderToPrint.deliveryFee || 0).toFixed(2).replace('.', ',')}<br>`;
      }
      if (orderToPrint.paymentConfirmed) {
        html += '<div style="font-size: 18px; font-weight: bold; margin-top: 5px;">Status Pagamento: PAGO</div>';
      }
      const safeTotal = Number(orderToPrint.total || 0);
      html += `<div style="font-size: 24px; font-weight: bold; margin-top: 10px; text-align: right;">Total: R$ ${safeTotal.toFixed(2).replace('.', ',')}</div>`;

      return html;
    };
    const printContents = createReceiptHTML();

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write('<html><head><title>Imprimir Pedido</title>');
    doc.write(`<style> 
      @page { size: auto; margin: 0mm; }
      body { font-family: 'Courier New', Courier, monospace; margin: 0; padding: 5px; font-size: 14px; line-height: 1.2; width: 72mm; }
      h2, h3, p, strong, span, br, div { margin: 0; padding: 0; }
      h2 { font-size: 18px; text-align: center; margin-bottom: 5px; font-weight: bold; }
      h3 { font-size: 16px; text-transform: uppercase; margin-top: 10px; margin-bottom: 5px; border-top: 2px solid #000; padding-top: 5px; font-weight: bold; }
      hr { border: none; border-top: 2px solid #000; margin: 10px 0; }
      div, h3, p { page-break-inside: avoid; }
      @media print { html, body { height: auto !important; overflow: visible !important; } }
    </style>`);
    doc.write('</head><body>');
    doc.write(printContents);
    doc.write('</body></html>');
    doc.close();

    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => { document.body.removeChild(iframe); }, 500);
  };

  // Se não estiver aberto, não renderiza nada.
  if (!isOpen) return null;

  // Define a classe do container principal com base na visualização
  const containerClass = isMobileView ? styles.mobileViewContainer : styles.modalOverlay;

  return (
    <div className={containerClass}>
      {/* Popup de Sucesso */}
      {showSuccess && (
        <div className={styles.successOverlay}>
          <div className={styles.successPopup}>
            <p>{mode === 'push_cart' ? 'Carrinho enviado para o cliente! 🛒' : (mode === 'edit' ? 'Pedido atualizado com sucesso! ✏️' : 'Venda registrada com sucesso! ✅')}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              {(mode === 'sale' || mode === 'edit') && (
                <button type="button" onClick={handlePrint} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '16px', fontWeight: 'bold' }}>
                  🖨️ Imprimir
                </button>
              )}
              <button onClick={handleCloseSuccess} className={styles.successOkBtn} style={{ padding: '8px 24px', fontSize: '16px' }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* O conteúdo do modal/view */}
      <div className={isMobileView ? styles.mobileContent : styles.modalContent}>
        {/* No modo mobile, o botão de fechar é um "voltar" no topo */}
        {isMobileView && <button onClick={onClose} className={styles.mobileCloseBtn}>&larr; Voltar para Pedidos</button>}
        {/* Título removido para ganhar espaço vertical, conforme solicitado */}
        <form onSubmit={handleSubmit}>
          {/* SEÇÃO 1: DADOS DO CLIENTE E ENTREGA */}
          <div className={styles.sectionBlock}>
            <h4 className={styles.sectionTitle}>Dados do Cliente {(mode === 'sale' || mode === 'edit') && '& Entrega'}</h4>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Nome do Cliente:</label>
                <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required placeholder="Ex: João da Silva" />
              </div>
              <div className={styles.formGroup}>
                <label>Telefone (Opcional):</label>
                <input type="text" value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="(00) 00000-0000" />
              </div>
            </div>

            {/* Esconde opções de entrega se for apenas montar carrinho */}
            {(mode === 'sale' || mode === 'edit') && <div className={`${styles.formRow} ${styles.deliveryRow}`}>
              <div className={styles.formGroup}>
                <label>Tipo de Venda:</label>
                <div className={styles.deliveryTabs}>
                  <button type="button" className={`${styles.tabButton} ${deliveryType === 'pickup' ? styles.activeTab : ''}`} onClick={() => handleDeliveryTypeChange('pickup')}>Retirada</button>
                  <button type="button" className={`${styles.tabButton} ${deliveryType === 'delivery' ? styles.activeTab : ''}`} onClick={() => handleDeliveryTypeChange('delivery')}>Entrega</button>
                  <button type="button" className={`${styles.tabButton} ${deliveryType === 'counter' ? styles.activeTab : ''}`} onClick={() => handleDeliveryTypeChange('counter')}>Balcão</button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>&nbsp;</label> {/* Espaçador para alinhar botão */}
                {!isPreOrderSale ? (
                  <button 
                    type="button" 
                    className={styles.preOrderBtn} 
                    onClick={() => setIsPreOrderSale(true)}
                  >
                    Agendar Encomenda
                  </button>
                ) : (
                  <div className={styles.preOrderWrapper}>
                    <input 
                      type="date" 
                      value={fulfillmentDate} 
                      onChange={(e) => setFulfillmentDate(e.target.value)} 
                      required 
                    />
                    <button 
                      type="button" 
                      className={styles.cancelDateBtn}
                      onClick={() => {
                        setIsPreOrderSale(false);
                        setFulfillmentDate('');
                      }}
                      title="Cancelar encomenda"
                    >
                      &times;
                    </button>
                  </div>
                )}
              </div>
            </div>}

            {(mode === 'sale' || mode === 'edit') && deliveryType === 'delivery' && (
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Rua e Número:</label>
                  <input type="text" value={address.streetAndNumber} onChange={(e) => setAddress({ ...address, streetAndNumber: e.target.value })} placeholder="Rua Exemplo, 123" />
                </div>
                <div className={styles.formGroup}>
                  <label>Bairro:</label>
                  <input 
                    type="text" 
                    value={address.neighborhood} 
                    onChange={(e) => setAddress({ ...address, neighborhood: e.target.value })} 
                    placeholder="Centro" 
                    list="neighborhood-suggestions"
                  />
                  <datalist id="neighborhood-suggestions">
                    {deliverySettings?.fees?.map((f, i) => (
                      <option key={i} value={f.neighborhood}>Taxa: R$ {f.fee.toFixed(2)}</option>
                    ))}
                  </datalist>
                </div>
              </div>
            )}

            {/* Checkbox para Entrega Rural */}
            {(mode === 'sale' || mode === 'edit') && deliveryType === 'delivery' && (
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" checked={isRuralDelivery} onChange={(e) => setIsRuralDelivery(e.target.checked)} />
                  Entrega Rural
                </label>
              </div>
            )}
          </div>

          {/* SEÇÃO 2: CARRINHO */}
          <div className={styles.cartSection}>
            <h4 className={styles.sectionTitle}>Carrinho de Compras</h4>
            
            {/* Navegação por Abas (Categorias) */}
            <div className={styles.categoryTabs}>
              <button 
                type="button" 
                onClick={() => setActiveCategory('assados')} 
                className={`${styles.tabButton} ${activeCategory === 'assados' ? styles.activeTab : ''}`}
              >
                Assados
              </button>
              <button 
                type="button" 
                onClick={() => setActiveCategory('acompanhamentos')} 
                className={`${styles.tabButton} ${activeCategory === 'acompanhamentos' ? styles.activeTab : ''}`}
              >
                Acompanhamentos
              </button>
              <button 
                type="button" 
                onClick={() => setActiveCategory('bebidas')} 
                className={`${styles.tabButton} ${activeCategory === 'bebidas' ? styles.activeTab : ''}`}
              >
                Bebidas
              </button>
            </div>

            <div className={styles.productSelectorsContainer}>
              <ProductSelector
                categoryProducts={categorizedProducts[activeCategory]}
                label={`Selecione ${activeCategory === 'assados' ? 'um assado' : activeCategory === 'bebidas' ? 'uma bebida' : 'acompanhamento'}...`}
                onAddProduct={handleAddProduct}
              />
            </div>
            <ul className={styles.cartList}>
              {cart.map((item, index) => (
                <li key={index}>
                  <span className={styles.cartItemDetails}>
                    {item.quantity}x {item.product} (R$ {(item.price * item.quantity).toFixed(2)})
                  </span>
                  <button type="button" onClick={() => handleRemoveItem(index)}>&times;</button>
                </li>
              ))}
            </ul>
            {cart.length > 0 && (
              <div className={styles.totalsSection}>
                <p>Subtotal: <span>R$ {subtotal.toFixed(2)}</span></p>
                {(mode === 'sale' || mode === 'edit') && deliveryType === 'delivery' && (
                  <p>Taxa de Entrega: <span>R$ {deliveryFee.toFixed(2)}</span></p>
                )}
                <p className={styles.total}>Total: <span>R$ {(mode === 'push_cart' ? subtotal : total).toFixed(2)}</span></p>
              </div>
            )}
          </div>

          {/* SEÇÃO 3: PAGAMENTO E OBSERVAÇÕES */}
          {(mode === 'sale' || mode === 'edit') && <div className={styles.sectionBlock}>
            <h4 className={styles.sectionTitle}>Pagamento & Detalhes</h4>
            
            {/* Linha combinada: Forma de Pagamento + Status */}
            <div className={styles.formRow} style={{ alignItems: 'flex-end' }}>
              <div className={styles.formGroup} style={{ flex: 2 }}>
                <div className={styles.deliveryTabs} style={{ width: '100%' }}>
                  <button type="button" className={`${styles.tabButton} ${paymentMethod === 'Dinheiro' ? styles.activeTab : ''}`} onClick={() => setPaymentMethod('Dinheiro')}>💵Dinheiro</button>
                  <button type="button" className={`${styles.tabButton} ${paymentMethod === 'Cartão' ? styles.activeTab : ''}`} onClick={() => setPaymentMethod('Cartão')}>💳Cartão</button>
                  <button type="button" className={`${styles.tabButton} ${paymentMethod === 'Pix' ? styles.activeTab : ''}`} onClick={() => setPaymentMethod('Pix')}>💠Pix</button>
                </div>
              </div>
              
              <div className={styles.formGroup} style={{ flex: 1 }}>
                  <label className={`${styles.checkboxLabel} ${styles.statusToggle}`} style={{ color: paymentConfirmed ? '#28a745' : '#6c757d' }}>
                  <input type="checkbox" checked={paymentConfirmed} onChange={(e) => setPaymentConfirmed(e.target.checked)} />
                  {paymentConfirmed ? 'PAGO' : 'PENDENTE'}
                </label>
              </div>
            </div>

            {paymentMethod === 'Dinheiro' && (
              <div className={styles.changeSection} style={{ marginBottom: '8px' }}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" checked={needsChange} onChange={(e) => setNeedsChange(e.target.checked)} />
                  Precisa de troco?
                </label>
                {needsChange && <input type="text" placeholder="R$ Valor" value={changeFor} onChange={(e) => setChangeFor(e.target.value)} />}
              </div>
            )}

            <div className={styles.formGroup}>
              <label>Observação:</label>
              <textarea value={observation} onChange={(e) => setObservation(e.target.value)} rows="1" placeholder="Ex: horário de entrega..."></textarea>
            </div>
          </div>}

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.modalActions}>
            {/* O botão de cancelar só aparece na versão desktop */}
            {!isMobileView && <button type="button" onClick={onClose} disabled={loading}>Cancelar</button>}
            <button type="submit" disabled={loading || cart.length === 0}>
              {loading ? 'Processando...' : (mode === 'push_cart' ? 'Enviar para Cliente 📲' : (mode === 'edit' ? 'Salvar Alterações' : 'Registrar Venda'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterSaleModal;